import { Component, OnInit } from '@angular/core';
import { ISave } from '@app-settings/shared/models/i-save.model';
import { SettingsService } from '@app-settings/shared/settings.service';
import { BaseComponent } from '@core/components/base-component';
import { ArrayHelper } from '@core/helpers/array.helper';
import { ClassificationHelper } from '@core/helpers/classification.helper';
import { EventHelper } from '@core/helpers/event.helper';
import { SortHelper } from '@core/helpers/sort.helper';
import { Classification } from '@interfaces/Classification';
import { CompanyCurrency } from '@interfaces/CompanyCurrency';
import { EstimateConfiguration } from '@interfaces/EstimateConfiguration';
import { TranslateService } from '@ngx-translate/core';
import { CurrencyService } from '@shared/services/currency.service';
import { EstimateSettingsService } from '@shared/services/estimate-settings.service';
import { EstimateService } from '@shared/services/estimate.service';
import { LicenseAppModule, LicenseService } from '@shared/services/license.service';
import { combineLatest } from 'rxjs';
import { ServiceTypePickerItem } from './service-type-picker/service-type-picker-item';
import { ServiceTypePickerService } from './service-type-picker/service-type-picker.service';

@Component({
  templateUrl: './estimate-general-settings.component.html',
  styleUrls: ['../settings.component.less', './estimate-general-settings.component.less']
})
export class EstimateGeneralSettingsComponent extends BaseComponent implements OnInit, ISave {
  private allCurrencies: CompanyCurrency[];
  public companies: string[];
  public estimateConfig: EstimateConfiguration;
  public isLoading = false;
  public showGeneral = true;
  public showBilling = true;
  public showCurrency = true;
  public showClasses = true;
  public showResources = true;
  public selectedCompanyKey: string;
  public selectedCompanyCurrencies: CompanyCurrency[];
  public selectedServiceType: ServiceTypePickerItem;
  public currentLang: string = this.translateService.currentLang;

  public includedClassifications: Classification[];
  public excludedClassifications: Classification[];
  public activeClassifications: Classification[];
  public inactiveClassifications: Classification[];
  public selectedClassifications: string[] = [];
  public selectedClassificationResources: string[] = [];
  private classifications: Classification[];

  public constructor(
    private currencyService: CurrencyService,
    private estimateSettingsService: EstimateSettingsService,
    private estimateService: EstimateService,
    private licenseService: LicenseService,
    private popSettingsService: SettingsService,
    private serviceTypePickerService: ServiceTypePickerService,
    private translateService: TranslateService
  ) {
    super();
  }

  public async ngOnInit() {
    const serviceTypes = await this.serviceTypePickerService.getServiceTypes();
    this.subscribe(
      this.licenseService.isLicenseValid(LicenseAppModule.Estimation).subscribe(status => {
        if (status !== 'valid') {
          return; // TODO do something
        }

        this.isLoading = true;
        this.subscribe(
          combineLatest([
            this.estimateSettingsService.getEstimateConfiguration(),
            this.currencyService.getCompanyCurrencies(),
            this.estimateService.getClassifications()
          ]).subscribe(([config, currencies, c]) => {
            // Config
            this.estimateConfig = config;
            if (!this.selectedServiceType && config.serviceOfferServiceTypeId) {
              this.selectedServiceType = serviceTypes.find(s => s.code === config.serviceOfferServiceTypeId);
            }

            // Company currencies
            this.allCurrencies = currencies;
            this.companies = Array.from(new Set(currencies.map(c => c.companyCode).sort()));
            [this.selectedCompanyKey] = this.companies;
            this.fillCurrencies(this.selectedCompanyKey);

            // Classifications
            this.classifications = c.filter(c => !ClassificationHelper.IsTous(c.code));

            const includedClassificationCodes = this.estimateSettingsService.getManagerClassificationCodes(
              this.estimateConfig
            );
            const activeClassificationCodes = this.estimateSettingsService.getActiveClassificationCodes(
              this.estimateConfig
            );

            this.excludedClassifications = [];
            this.includedClassifications = [];
            this.inactiveClassifications = [];
            this.activeClassifications = [];

            for (const classification of this.classifications) {
              if (!includedClassificationCodes.includes(classification.code)) {
                this.excludedClassifications.push(classification);
              } else {
                this.includedClassifications.push(classification);
              }
              if (!activeClassificationCodes.includes(classification.code)) {
                this.inactiveClassifications.push(classification);
              } else {
                this.activeClassifications.push(classification);
              }
            }
            this.sortClasses();
            this.sortClasses();

            this.isLoading = false;
          })
        );
      })
    );
  }

  public changed() {
    this.estimateConfig.managerClassificationCodes = this.includedClassifications.map(c => c.code).join(';');
    this.estimateConfig.activeClassificationCodes = this.activeClassifications.map(c => c.code).join(';');

    this.popSettingsService.needsSave();
    this.popSettingsService.onSave(this);
  }

  public activateInternalFeesChanged() {
    this.estimateConfig.hideSectionInternalFees = !this.estimateConfig.activateInternalFees;
    this.changed();
  }

  public fillCurrencies(company: string) {
    this.selectedCompanyCurrencies = this.allCurrencies
      .filter(currency => currency.companyCode === company)
      .sort((a, b) => {
        if (a.isFunctional) {
          return -1; // functional on top
        }
        if (b.isFunctional) {
          return 1; // functional on top
        }
        const aCode = a.currencyCode;
        const bCode = b.currencyCode;
        if (aCode < bCode) return -1;
        if (aCode > bCode) return 1;
        return 0;
      });
  }

  public async onSave() {
    await this.estimateSettingsService.setEstimateConfiguration(this.estimateConfig);

    await this.currencyService.saveChanges();
  }

  public onSelectedServiceTypeChange() {
    if (!this.estimateConfig || this.estimateConfig?.serviceOfferServiceTypeId === this.selectedServiceType?.code) {
      return;
    }

    this.estimateConfig.serviceOfferServiceTypeId = this.selectedServiceType?.code ?? '';
    this.changed();
  }

  public selectAllClassIncluded(event: Event) {
    this.selectedClassifications = this.activeClassifications.map(c => c.code);
    EventHelper.stopEventHere(event);
  }
  public selectAllClassExcluded(event: Event) {
    this.selectedClassifications = this.inactiveClassifications.map(c => c.code);
    EventHelper.stopEventHere(event);
  }
  public selectAllResoucesIncluded(event: Event) {
    this.selectedClassificationResources = this.includedClassifications.map(c => c.code);
    EventHelper.stopEventHere(event);
  }
  public selectAllResourceExcluded(event: Event) {
    this.selectedClassificationResources = this.excludedClassifications.map(c => c.code);
    EventHelper.stopEventHere(event);
  }

  public selectClass(classCode: string) {
    const index = this.selectedClassifications.indexOf(classCode);
    if (index === -1) {
      this.selectedClassifications.push(classCode);
    } else {
      this.selectedClassifications.splice(index, 1);
    }
  }

  public selectResource(classCode: string) {
    const index = this.selectedClassificationResources.indexOf(classCode);
    if (index === -1) {
      this.selectedClassificationResources.push(classCode);
    } else {
      this.selectedClassificationResources.splice(index, 1);
    }
  }

  public switchResources() {
    for (const classCode of this.selectedClassificationResources) {
      // get classificaiton
      const classification = this.classifications.find(c => c.code.toUpperCase() === classCode);
      if (!classification) {
        continue;
      }

      if (this.includedClassifications.includes(classification)) {
        //switch from include to exclude
        ArrayHelper.removeItem(this.includedClassifications, classification);
        this.excludedClassifications.push(classification);
      } else {
        // switch from exclude to include
        ArrayHelper.removeItem(this.excludedClassifications, classification);
        this.includedClassifications.push(classification);
      }
    }
    this.selectedClassificationResources = [];
    this.sortResources();
    this.changed();
  }

  public switchClassifications() {
    for (const classCode of this.selectedClassifications) {
      // get classificaiton
      const classification = this.classifications.find(c => c.code.toUpperCase() === classCode);
      if (!classification) {
        continue;
      }

      if (this.activeClassifications.includes(classification)) {
        //switch from active to inactive
        ArrayHelper.removeItem(this.activeClassifications, classification);
        this.inactiveClassifications.push(classification);
      } else {
        // switch from inactive to active
        ArrayHelper.removeItem(this.inactiveClassifications, classification);
        this.activeClassifications.push(classification);
      }
    }

    this.selectedClassifications = [];
    this.sortClasses();
    this.changed();
  }

  private sortResources() {
    SortHelper.sortArrayByCode(this.includedClassifications);
    SortHelper.sortArrayByCode(this.excludedClassifications);
  }

  private sortClasses() {
    SortHelper.sortArrayByCode(this.activeClassifications);
    SortHelper.sortArrayByCode(this.inactiveClassifications);
  }
}
